import { Link } from "react-router-dom";
import { BadgeCheck, GraduationCap, Mail, Phone, ShieldCheck } from "lucide-react";
import Logo from "@/components/Logo";
import { useLang } from "@/lib/i18n";

const CONTENT = {
  en: {
    tagline: "Individual psychotherapy — warm, unhurried, and confidential, online.",
    registered: "Registered and supervised practice.",
    credentials: "Credentials",
    eduTitle: "Education",
    contact: "Get in touch",
    links: [
      { to: "/about", label: "About me" },
      { to: "/services", label: "Services & specialisations" },
      { to: "/faq", label: "FAQ & practical info" },
      { to: "/intake", label: "Book a consultation" },
    ],
    copyright: "Individual psychotherapy — online sessions",
    admin: "Admin",
    crisis:
      "If you are in immediate crisis, please call your local emergency number or a crisis line.",
    registrations: [
      { body: "BACP — British Association for Counselling & Psychotherapy", ref: "United Kingdom · Registration 420016" },
      { body: "Licensed Psychologist", ref: "Greece · Registration 274123" },
      { body: "British Psychological Society — Member", ref: "Membership 840906" },
    ],
    education: [
      { body: "MSc Counseling and Psychotherapy", ref: "The American University of Greece" },
      { body: "BSc (Hons) Psychology", ref: "The Open University, United Kingdom" },
    ],
  },
  el: {
    tagline: "Ατομική ψυχοθεραπεία — ζεστή, χωρίς βιασύνη και εμπιστευτική. Online.",
    registered: "Εγγεγραμμένη και εποπτευόμενη πρακτική.",
    credentials: "Πιστοποιήσεις",
    eduTitle: "Σπουδές",
    contact: "Επικοινωνία",
    links: [
      { to: "/about", label: "Για εμένα" },
      { to: "/services", label: "Υπηρεσίες & εξειδικεύσεις" },
      { to: "/faq", label: "Συχνές ερωτήσεις & πρακτικά" },
      { to: "/intake", label: "Κλείστε ραντεβού" },
    ],
    copyright: "Ατομική ψυχοθεραπεία — συνεδρίες online",
    admin: "Διαχείριση",
    crisis:
      "Σε περίπτωση άμεσης κρίσης, παρακαλώ καλέστε το τοπικό νούμερο έκτακτης ανάγκης ή τη Γραμμή Ψυχοκοινωνικής Υποστήριξης – 10306.",
    registrations: [
      { body: "BACP — British Association for Counselling & Psychotherapy", ref: "Ηνωμένο Βασίλειο · Μητρώο 420016" },
      { body: "Πτυχιούχος Ψυχολόγος", ref: "Ελλάδα · Μητρώο 274123" },
      { body: "British Psychological Society — Μέλος", ref: "Μητρώο Μέλους 840906" },
    ],
    education: [
      { body: "MSc Counseling and Psychotherapy", ref: "The American University of Greece" },
      { body: "BSc (Hons) Psychology", ref: "The Open University, Ηνωμένο Βασίλειο" },
    ],
  },
};

const Footer = () => {
  const { lang } = useLang();
  const c = CONTENT[lang];

  return (
    <footer className="mt-auto border-t border-sage-600/10 bg-secondary/50" data-testid="site-footer">
      <div className="container-x py-16 md:py-20">
        <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-3">
          <div className="space-y-5">
            <Logo light={false} />
            <p className="max-w-xs text-sm leading-relaxed text-muted-foreground">{c.tagline}</p>
            <p className="flex items-start gap-2 text-xs leading-relaxed text-muted-foreground">
              <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-sage-500" />
              {c.registered}
            </p>
          </div>

          <div className="space-y-4" data-testid="footer-credentials">
            <p className="eyebrow">{c.credentials}</p>
            <ul className="space-y-4 text-sm">
              {c.registrations.map((r) => (
                <li key={r.body} className="flex items-start gap-2">
                  <BadgeCheck className="mt-0.5 h-4 w-4 shrink-0 text-clay" strokeWidth={1.75} />
                  <span className="leading-relaxed text-foreground/85">
                    {r.body}
                    <span className="block text-xs text-muted-foreground">{r.ref}</span>
                  </span>
                </li>
              ))}
            </ul>
            <div className="pt-1" data-testid="footer-bacp-logo">
              <img
                src="/bacp-logo.webp"
                alt="BACP Registered Member 420016 — MBACP"
                className="h-12 w-auto rounded-xl bg-white p-2 ring-1 ring-sage-600/10"
                loading="lazy"
              />
            </div>
            <p className="flex items-center gap-2 pt-2 text-xs font-semibold uppercase tracking-[0.18em] text-sage-600">
              <GraduationCap className="h-4 w-4 text-clay" /> {c.eduTitle}
            </p>
            <ul className="space-y-3 text-sm">
              {c.education.map((d) => (
                <li key={d.body} className="flex items-start gap-2">
                  <GraduationCap className="mt-0.5 h-4 w-4 shrink-0 text-clay" strokeWidth={1.75} />
                  <span className="leading-relaxed text-foreground/85">
                    {d.body}
                    <span className="block text-xs text-muted-foreground">{d.ref}</span>
                  </span>
                </li>
              ))}
            </ul>
          </div>

          <div className="space-y-4" data-testid="footer-contact">
            <p className="eyebrow">{c.contact}</p>
            <ul className="space-y-3 text-sm text-foreground/85">
              <li>
                <a
                  href="mailto:info@ioannistsiros.com"
                  data-testid="footer-email-link"
                  className="link-underline inline-flex items-center gap-2 hover:text-sage-700"
                >
                  <Mail className="h-4 w-4 shrink-0 text-sage-500" strokeWidth={1.75} />
                  info@ioannistsiros.com
                </a>
              </li>
              <li>
                <a
                  href="tel:+306982052345"
                  data-testid="footer-phone-link"
                  className="link-underline inline-flex items-center gap-2 hover:text-sage-700"
                >
                  <Phone className="h-4 w-4 shrink-0 text-sage-500" strokeWidth={1.75} />
                  +30 698 205 2345
                </a>
              </li>
            </ul>
            <nav className="pt-2" aria-label="Footer">
              <ul className="space-y-2 text-sm">
                {c.links.map((l) => (
                  <li key={l.to}>
                    <Link className="link-underline text-foreground/80 hover:text-sage-700" to={l.to}>
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          </div>
        </div>

        <div className="mt-14 flex flex-col gap-3 border-t border-sage-600/10 pt-8 text-xs text-muted-foreground md:flex-row md:items-center md:justify-between">
          <p>
            &copy; {new Date().getFullYear()} Ioannis Tsiros — Psychotherapy &middot; {c.copyright}
            {" · "}
            <Link
              to="/admin"
              data-testid="footer-admin-link"
              className="transition-colors duration-200 hover:text-sage-700"
            >
              {c.admin}
            </Link>
          </p>
          <p>{c.crisis}</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
